import * as React from 'react';
import { Button, Input, Modal, TextBox } from '../..';
import { useAuth } from '../../../context/authContext';
import {
  Department,
  storePreferenceFormAction,
} from '../../../context/storeContext';
import useValidator from '../../../hooks/useValidator';
import { Option } from '../../Inputs/SelectInput/SelectInput';

type ModalProps = {
  onCloseModal: () => void;
  onSetAddedValue: (option: Option) => void;
  departments: Department[];
};

function DepartmentAdd({
  onCloseModal,
  onSetAddedValue,
  departments,
}: ModalProps) {
  const [{ user, storePreferences }, dispatch] = useAuth();
  const [department, setDepartment] = React.useState({
    name: '',
    description: '',
  });
  const [status, setStatus] = React.useState<
    'IDLE' | 'LOADING' | 'RESOLVED' | 'REJECTED'
  >('IDLE');
  const [validator, showValidationMessage] = useValidator({});

  const handleDepartmentInputChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = event.target;

    setDepartment(fndDepartment => ({
      ...fndDepartment,
      [name]: value,
    }));
  };

  const handleDepartmentSave = async () => {
    if (!validator.allValid()) {
      showValidationMessage(true);
      return;
    }

    setStatus('LOADING');

    const storeId = user?.defaultStore?.id;

    const { department: getAddedDepartment } = (await storePreferenceFormAction(
      `/department?store=${storeId}`,
      'post',
      { ...department, storeId },
    )) as any;

    if (storePreferences && getAddedDepartment) {
      dispatch({
        type: 'SET_STORE_PREFERENCES',
        payload: {
          ...storePreferences,
          departments:
            storePreferences.departments.length > 0
              ? [getAddedDepartment, ...storePreferences.departments]
              : [getAddedDepartment],
        },
      });
    }

    onSetAddedValue({
      value: getAddedDepartment.id,
      label: getAddedDepartment.name,
    });

    setStatus('RESOLVED');
    onCloseModal();
  };

  return (
    <Modal onClose={onCloseModal} show>
      <div className="rounded-md bg-white tablet:w-[500px]">
        <form>
          <div className="flex flex-col p-5">
            <h3
              className="text-pos-black text-[20px] font-semibold mb-2
              inline-flex whitespace-pre"
            >
              Add New Department
            </h3>
            <div className="text-pos-black text-sm mt-2 mb-2 font-normal space-y-4">
              <div>
                <label htmlFor="name" className="text-xbase block mb-1">
                  Name{' '}
                  <span className="text-xs font-light text-pos-red">*</span>
                </label>
                <Input
                  id="name"
                  name="name"
                  value={department.name}
                  onChange={handleDepartmentInputChange}
                />
                {validator.message('name', department.name, 'required|min:3', {
                  messages: {
                    required: 'Department name is required.',
                  },
                  className:
                    'text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1',
                })}
              </div>
              <div>
                <label htmlFor="description" className="text-xbase block mb-1">
                  Description
                </label>
                <TextBox
                  id="description"
                  name="description"
                  value={department.description}
                  rows={3}
                  onChange={handleDepartmentInputChange}
                />
              </div>
            </div>
            <div className="mt-4 text-center flex flex-row ml-auto space-x-4">
              <Button
                label="Save"
                onClick={handleDepartmentSave}
                type="button"
                disabled={status === 'LOADING'}
                loading={status === 'LOADING'}
                className="!bg-pos-green border border-[rgba(27,31,36,0.15)] min-w-[150px]  h-[40px]
                      items-center justify-center text-white text-xbase
                      shadow-[0_1px_0_rgba(27,31,36,0.1),inset_0_1px_0_rgba(255,255,255,0.03)] shrink-0 font-medium"
              />
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
}

export default DepartmentAdd;
